/**
 * @prettier
 * THIS STORE IS AUTO-GENERATED - DO NOT MODIFY ANYTHING IN HERE!
 */
import {
  apiEmployeesGetCollection,
  apiEmployeesPost,
  apiEmployeesIdGet,
  apiEmployeesIdPut,
  apiEmployeesIdavatarDelete,
  apiEmployeesIdavatarPost,
} from "@/api";
import { useLogger, type Logger } from "@/composables/useLogger";
import { useMercure, type MercureUnsubscriber } from "@/composables/useMercure";
import { type StoreCache, useStoreCache } from "@/composables/useStoreCache";
import { fromIri, iri2id, isIri } from "@/helpers/iri";
import { useEventBus } from "@vueuse/core";
import { Semaphore } from "async-mutex";
import { keyBy, uniq, without, forEach, fromPairs } from "lodash-es";
import { AxiosResponse, AxiosError } from "axios";
import {
  checkIriArray,
  HydraToPagination,
  HydraToIriDictionary,
  HydraToIriMap,
  filterAsync,
  arrayToObject,
  RequestOptions,
  defaultRequestOptionsMerge,
  storeMethodArgumentsCompatibilityLayer,
} from "@/stores";
import { defineStore, DefineStoreOptions, StateTree, Store } from "pinia";
import type {
  ApiEmployeesGetCollectionData,
  ApiEmployeesGetCollectionResponse,
  ApiEmployeesPostData,
  ApiEmployeesPostResponse,
  ApiEmployeesIdGetData,
  ApiEmployeesIdGetResponse,
  ApiEmployeesIdPutData,
  ApiEmployeesIdPutResponse,
  ApiEmployeesIdavatarDeleteData,
  ApiEmployeesIdavatarDeleteResponse,
  ApiEmployeesIdavatarPostData,
  ApiEmployeesIdavatarPostResponse,
} from "@/api";

import type { Pagination, JsonldCollection } from "@/stores";

//#region Live stream handler
useEventBus("mercure-connected").on(() => {
  const mercureUnsubscriber: MercureUnsubscriber = useMercure().subscribe<{
    "@id": string;
  }>("Employee", async (data: { "@id": string }) => {
    if (data["@id"])
      await useGeneratedEmployeeStore().$cache.set(
        iri2id(data["@id"]),
        data,
        "item",
      );
  });
  useEventBus("mercure-disconnected").once(() => {
    // this.$logger.debug(`unsubscribed to real-time updates`);
    mercureUnsubscriber();
  });
});
//#endregion Live stream handler
const storeId = "employee-store.gen";
const logger = useLogger(storeId);
//const cache = useStoreCache(storeId)
export const useGeneratedEmployeeStore = defineStore(storeId, {
  state: () => ({
    loading: false,
    $iriPrefix: "/api/employees",
    $cache: useStoreCache(storeId),
  }),
  actions: {
    /*
     * GetCnt: 1
     * CollectionCnt: 1
     * PatchCnt: 0
     * PostCnt: 2
     * PutCnt: 1
     * DeleteCnt: 1
     */

    async fetchById<ThrowGlobalError extends boolean = true>(
      id: string,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "fetchBy");
      options = defaultRequestOptionsMerge(options);
      if (options?.forceRefresh)
        await this.$cache.delete(
          id + ":" + (options?.context ?? "default"),
          "item",
        );
      return await this.$cache.getOrCreate<ApiEmployeesIdGetResponse>(
        id + ":" + (options?.context ?? "default"),
        async () => {
          const promise = (await apiEmployeesIdGet<ThrowGlobalError>({
            ...(options?.context
              ? { query: { context: options?.context } }
              : {}),
            path: { id: id as unknown as string },
            throwOnError: options.throwGlobalError,
          })) as AxiosResponse<ApiEmployeesIdGetResponse>;
          if (!(promise.status >= 200 && promise.status < 300))
            throw new Error("ERROR_SKIP_CACHE", { cause: promise });
          return promise.data;
        },
        "item",
      );
    },
    async fetchByIdRaw<ThrowGlobalError extends boolean = true>(
      id: string,
      context?: string,
    ) {
      return await apiEmployeesIdGet<ThrowGlobalError>({
        ...(context ? { query: { context } } : {}),
        path: { id: id },
      });
    },

    fetchByIri<ThrowGlobalError extends boolean = true>(
      iri: string,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "fetchByIri");
      options = defaultRequestOptionsMerge(options);
      if (!iri) {
        debugger;
        throw new Error("No Iri");
      }
      const id = iri2id(iri, false);
      return this.fetchById<ThrowGlobalError>(id, options);
    },

    /**
{"name":"apiEmployeesGetCollection","tag":"Employee","storeName":"Employee","requestType":"ApiEmployeesGetCollectionData","responseType":"ApiEmployeesGetCollectionResponse","errorType":"unknown","apiFunctionName":"apiEmployeesGetCollection","requestImportName":"ApiEmployeesGetCollectionData","responseImportName":"ApiEmployeesGetCollectionResponse","actionType":"collection","actionName":"fetchAll","friendlyName":"fetchAll","suffix":""}
*/
    async findAll<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData | null,
      options?: RequestOptions<ThrowGlobalError>,
    ): Promise<Ref<ApiEmployeesGetCollectionResponse["hydra:member"]>> {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "findAll");
      options = defaultRequestOptionsMerge(options);
      payload ??= {};
      payload.query ??= {};
      payload.query.page ??= 1;
      payload.query.itemsPerPage ??= 100;
      if (payload.query.itemsPerPage === 0) return ref([]);
      if (options?.context) payload.query.context = options.context;
      if (options?.forceRefresh)
        await this.$cache.delete(payload, "collection");
      const res = await this.$cache.getOrCreate<
        ApiEmployeesGetCollectionResponse["hydra:member"]
      >(
        payload,
        async () => {
          const promise = (await apiEmployeesGetCollection<ThrowGlobalError>({
            ...payload,
            ...(options?.context
              ? { query: { context: options?.context } }
              : {}),
            throwOnError: options.throwGlobalError,
          })) as AxiosResponse<
            ApiEmployeesGetCollectionResponse["hydra:member"]
          >;
          if (!(promise.status >= 200 && promise.status < 300))
            throw new Error("ERROR_SKIP_CACHE", { cause: promise });
          return promise.data["hydra:member"]
            ? Array.from(promise.data["hydra:member"])
            : [];
        },
        "collection",
      );
      if (typeof res.value === "object" && !payload.query["properties[]"]) {
        const data =
          res.value as ApiEmployeesGetCollectionResponse["hydra:member"];
        await Promise.all(
          data.map((jsonld) => {
            if (jsonld["@id"]) {
              return this.$cache.set(iri2id(jsonld["@id"]), jsonld, "item");
            }
          }),
        );
      }
      return res as Ref<ApiEmployeesGetCollectionResponse["hydra:member"]>;
    },
    async findAllHydra<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData | null,
      options?: RequestOptions<ThrowGlobalError>,
    ): Promise<Ref<ApiEmployeesGetCollectionResponse>> {
      payload ??= {};
      payload.query ??= {};
      payload.query.page ??= 1;
      payload.query.itemsPerPage ??= 100;
      if (options?.context) payload.query.context = options.context;
      if (options?.forceRefresh) await this.$cache.delete(payload, "hydra");
      const res =
        await this.$cache.getOrCreate<ApiEmployeesGetCollectionResponse>(
          payload,
          async () => {
            const d = await apiEmployeesGetCollection<ThrowGlobalError>(
              payload,
            ).then((d) => d.data);
            return d;
          },
          "hydra",
        );
      if (typeof res.value === "object" && !payload.query["properties[]"]) {
        const data = res.value["hydra:member"];
        await Promise.all(
          data.map((jsonld) => {
            if (jsonld["@id"]) {
              return this.$cache.set(iri2id(jsonld["@id"]), jsonld, "item");
            }
          }),
        );
      }
      return res;
    },
    async find<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "find");
      if (!payload) payload = { query: {} };
      if (!payload.query) payload.query = {};
      payload.query.page = 1;
      payload.query.itemsPerPage = 1;
      if (options?.context) payload.query.context = options.context;
      const res = await this.findAll<ThrowGlobalError>(payload, options);
      if (res.value.length > 1)
        logger.warn("Find() returned more than one result!");
      return computed(() => (res.value.length > 0 ? res.value[0] : undefined));
    },
    /* {"name":"apiEmployeesGetCollection","tag":"Employee","storeName":"Employee","requestType":"ApiEmployeesGetCollectionData","responseType":"ApiEmployeesGetCollectionResponse","errorType":"unknown","apiFunctionName":"apiEmployeesGetCollection","requestImportName":"ApiEmployeesGetCollectionData","responseImportName":"ApiEmployeesGetCollectionResponse","actionType":"collection","actionName":"fetchAll","friendlyName":"fetchAll","suffix":""} */
    async findAllWithPagination<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData | null,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "findAllWithPagination",
      );
      const result = await this.findAllHydra<ThrowGlobalError>(
        payload,
        options,
      );
      return HydraToPagination<(typeof result.value)["hydra:member"][number]>(
        result,
      );
    },
    async findAllAsIriDictionary<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData | null,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "findAllAsIriDictionary",
      );
      const result = await this.findAllHydra<ThrowGlobalError>(
        payload,
        options,
      );
      return HydraToIriDictionary<
        (typeof result.value)["hydra:member"][number]
      >(result);
    },
    async findAllAsMap<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesGetCollectionData | null,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "findAllAsMap",
      );
      const result = await this.findAllHydra<ThrowGlobalError>(
        payload,
        options,
      );
      return HydraToIriMap<(typeof result.value)["hydra:member"][number]>(
        result,
      );
    },

    async fetchAllByIri<ThrowGlobalError extends boolean = true>(
      iris: string[],
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "fetchAllByIri",
      );
      const result: Ref<unknown[]> = ref([]);
      const iriCheck = checkIriArray(iris, this.$iriPrefix);
      if (iriCheck.inputCnt != iriCheck.outputCnt)
        logger.warn(
          `At least one given iri is not valid or does not match api prefix (${this.$iriPrefix})!`,
          iris,
        );
      if (iriCheck.outputCnt == 0) return result;
      const allIds = iriCheck.iris.map((iri) => fromIri<string>(iri)?.id!);
      const dataset = options?.forceRefresh
        ? allIds.map((id) => [id, undefined])
        : await Promise.all(
            allIds.map(async (id) => [id, await this.$cache.get(id, "item")]),
          );
      const missingInCache = dataset
        .filter((pair) => !pair[1])
        .map((pair) => pair[0]);
      const fetched = await this.findAll<ThrowGlobalError>(
        {
          query: {
            itemsPerPage: missingInCache.length,
            "id[]": missingInCache as string[],
          },
        },
        options,
      ).then((d) => d.value);
      //now rebuild return
      for (const id of allIds) {
        const dsMatch = dataset.find((pair) => pair[0] == id) as unknown[];
        if (dsMatch[1]) {
          result.value.push(dsMatch[1]);
          continue;
        }
        if (fetched && fetched.length > 0) {
          /*
           */
          const fetchedItem = fetched.find(
            (itm) => (itm.id as string) == (id as string),
          );
          if (!fetchedItem) {
            debugger;
          }
          result.value.push(fetchedItem);
        }
      }
      return result;
    },

    async fetchAllByIriAsDictionary<ThrowGlobalError extends boolean = true>(
      iris: string[],
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "fetchAllByIriAsDictionary",
      );
      const data = await this.fetchAllByIri<ThrowGlobalError>(iris, options);
      const res = keyBy(
        data.value,
        (e: ApiEmployeesIdGetResponse) => e["@id"],
      ) as Record<string, ApiEmployeesIdGetResponse>;
      return readonly(ref(res));
    },
    async fetchAllByIriAsMap<ThrowGlobalError extends boolean = true>(
      iris: string[],
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(
        arguments,
        "fetchAllByIriAsMap",
      );
      const result = new Map<string, ApiEmployeesIdGetResponse>();
      const rawData = await this.fetchAllByIri<ThrowGlobalError>(iris, options);
      const data = keyBy(
        rawData.value,
        (e: ApiEmployeesIdGetResponse) => e["@id"],
      ) as Record<string, ApiEmployeesIdGetResponse>;
      forEach(data, (v: ApiEmployeesIdGetResponse, k: string) =>
        result.set(k, v),
      );
      return readonly(ref(result));
    },

    /* {"name":"apiEmployeesPost","tag":"Employee","storeName":"Employee","requestType":"ApiEmployeesPostData","responseType":"ApiEmployeesPostResponse","errorType":"ApiEmployeesPostError","apiFunctionName":"apiEmployeesPost","requestImportName":"ApiEmployeesPostData","responseImportName":"ApiEmployeesPostResponse","actionType":"post","actionName":"post","friendlyName":"post","suffix":""} */
    async post<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesPostData,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "post");
      options = defaultRequestOptionsMerge(options);
      return await apiEmployeesPost<ThrowGlobalError>({
        ...payload,
        throwOnError: options.throwGlobalError,
      }).then((d) => d.data);
    },
    /* {"name":"apiEmployeesIdavatarPost","tag":"Employee","storeName":"Employee","requestType":"ApiEmployeesIdavatarPostData","responseType":"ApiEmployeesIdavatarPostResponse","errorType":"ApiEmployeesIdavatarPostError","apiFunctionName":"apiEmployeesIdavatarPost","requestImportName":"ApiEmployeesIdavatarPostData","responseImportName":"ApiEmployeesIdavatarPostResponse","actionType":"post","actionName":"post","friendlyName":"post","identifierName":"id","identifierType":"string","suffix":"Idavatar","identifierNameCapitalized":"Id"} */
    async postIdavatar<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesIdavatarPostData,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "post");
      options = defaultRequestOptionsMerge(options);
      return await apiEmployeesIdavatarPost<ThrowGlobalError>({
        ...payload,
        throwOnError: options.throwGlobalError,
      }).then((d) => d.data);
    },

    /* {"name":"apiEmployeesIdPut","tag":"Employee","storeName":"Employee","requestType":"ApiEmployeesIdPutData","responseType":"ApiEmployeesIdPutResponse","errorType":"ApiEmployeesIdPutError","apiFunctionName":"apiEmployeesIdPut","requestImportName":"ApiEmployeesIdPutData","responseImportName":"ApiEmployeesIdPutResponse","actionType":"update","actionName":"put","friendlyName":"put","identifierName":"id","identifierType":"string","suffix":"Id","identifierNameCapitalized":"Id"} */
    async putId<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesIdPutData,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "put");
      options = defaultRequestOptionsMerge(options);
      if (isIri(payload.path.id)) {
        payload.path.id = iri2id(payload.path.id);
        console.log("IRI given where Id was expected! Please fix!");
      }
      return await apiEmployeesIdPut<ThrowGlobalError>({
        ...payload,
        throwOnError: options.throwGlobalError,
      }).then((d) => d.data);
    },

    async delete<ThrowGlobalError extends boolean = true>(
      payload: ApiEmployeesIdavatarDeleteData,
      options?: RequestOptions<ThrowGlobalError>,
    ) {
      options = storeMethodArgumentsCompatibilityLayer(arguments, "delete");
      options = defaultRequestOptionsMerge(options);
      const res = await apiEmployeesIdavatarDelete<ThrowGlobalError>({
        ...payload,
        throwOnError: options.throwGlobalError,
      });
      await this.$cache.delete(payload, "item");
      return res;
    },
  },
});
